<script>
import {
  CdrButton,
  CdrGrid,
  CdrText,
} from '@rei/cedar';
import analytics from '../../common/analytics';
import ButtonImgComponent from '../common/button-img/ButtonImgComponent.vue';
import CONSTANTS from '../../utils/constants';
import specialGiftCard from '../../utils/specialGiftcard';

export default {
  name: 'GiftcardListComponent',
  components: {
    ButtonImgComponent,
    CdrButton,
    CdrGrid,
    CdrText,
  },
  props: {
    deliveryMethod: {
      type: String,
      required: true,
    },
    cards: {
      type: Object,
      required: true,
    },
    isMobile: {
      type: Boolean,
      required: true,
    },
    tabDeliveryPosition: {
      type: Number,
      required: true,
    },
    isFormOpen: {
      type: Boolean,
      required: true,
    },
    preFillValue: {
      type: String,
      default: '0.00',
    },
  },
  emits: ['onSelectGiftCard'],
  data() {
    return {
      analytics,
      methodType: this.deliveryMethod === 'EMAIL' ? 'emailCards' : 'mailCards',
      giftCards: this.cards.emailCards,
      tempGiftCard: this.cards.emailCards,
      totalItem: this.cards.emailCards.length,
      isGiftCardSelect: false,
      activeGiftCard: {},
      ListHeight: '1430px',
      timeout: null,
    };
  },
  computed: {
    getTitleShowMore() {
      return (
        this.totalItem > this.giftCards.length ? CONSTANTS.BTN_MSG_SHOW : CONSTANTS.BTN_MSG_FEWER
      );
    },
    giftCardListHeight() {
      const isSpecialCardSelect = Object.prototype
        .hasOwnProperty.call(specialGiftCard, this.activeGiftCard.mediaCode);
      return (
        !this.isMobile && this.deliveryMethod === 'EMAIL'
          ? `${1430 + (isSpecialCardSelect ? 70 : 0)}px`
          : `${1078 + (isSpecialCardSelect ? 70 : 0)}px`
      );
    },
  },
  watch: {
    deliveryMethod(newValue, oldValue) {
      if (newValue === oldValue) return;
      this.methodType = newValue === 'EMAIL' ? 'emailCards' : 'mailCards';
      this.resetData();
      this.setGiftCardList(this.methodType, this.isMobile);
    },
    isMobile(newValue) {
      this.setGiftCardList(this.methodType, newValue);
    },
    isFormOpen(newValue) {
      if (!newValue) {
        this.giftCards = this.giftCards.map((giftCard) => ({ ...giftCard, selected: newValue }));
        const index = this.giftCards
          .findIndex((object) => object.mediaCode === this.activeGiftCard.mediaCode);
        this.calculateGiftCardPositionOffset({
          ...this.activeGiftCard,
          index: index + 1,
          selected: newValue,
        });
      }
      this.isGiftCardSelect = newValue;
    },
  },
  mounted() {
    this.setGiftCardList(this.methodType, this.isMobile);
    if (this.preFillValue && this.preFillValue !== '0.00') {
      this.selectGiftCard(this.giftCards[0]);
    }
  },
  beforeUnmount() {
    if (this.timeout) clearTimeout(this.timeout);
  },
  methods: {
    setGiftCardList(methodType = 'emailCards', isMobile = false) {
      const cardsList = this.isGiftCardSelect
        ? {
          ...this.cards,
          [methodType]: this.cards[methodType].map(
            (card) => ({ ...card, selected: card.mediaCode === this.activeGiftCard.mediaCode })
          ),
        } : this.cards;
      this.giftCards = isMobile
        ? cardsList[methodType].slice(0, 12) : cardsList[methodType];
      this.tempGiftCard = cardsList[methodType];
      this.totalItem = cardsList[methodType].length;
    },
    selectGiftCard(choosenGiftCard) {
      this.activeGiftCard = { ...choosenGiftCard, selected: !choosenGiftCard.selected };
      this.giftCards = this.giftCards.map((giftCard) => ({
        ...giftCard, selected: giftCard.mediaCode === this.activeGiftCard.mediaCode,
      }));
      this.$emit('onSelectGiftCard', this.activeGiftCard);
      if (!this.isMobile) window.scrollTo({ top: 480 });
      if (!this.isMobile && !this.isGiftCardSelect) {
        this.calculateGiftCardPositionOffset(this.activeGiftCard);
      }
      this.isGiftCardSelect = this.activeGiftCard.selected;
    },
    resetData() {
      this.giftCards = [];
      this.tempGiftCard = [];
      this.isGiftCardSelect = false;
      this.activeGiftCard = {};
    },
    sendAnalyticClickEvent(eventName) {
      this.analytics.click({ linkName: `${this.$props.siteId}:${this.$props.pageDescription}_${eventName}` });
    },
    showLessMoreGiftCards() {
      if (this.totalItem > this.giftCards.length) {
        this.giftCards = this.giftCards.concat(
          this.tempGiftCard.slice(this.giftCards.length, this.giftCards.length + 12)
        );
      } else {
        this.giftCards = this.tempGiftCard.slice(0, 12);
        window.scrollTo({ top: this.tabDeliveryPosition - 40 });
      }
    },
    calculateGiftCardPositionOffset(giftCardSelect) {
      const giftCardHeight = giftCardSelect.selected ? 183 : 180;
      const totalColumns = giftCardSelect.selected ? 2 : 4;
      const totalRows = Math.floor(this.giftCards.length / totalColumns);
      const positionGiftCard = Math.floor(
        (giftCardSelect.index * (totalRows)) / this.giftCards.length
      );
      const scrollTop = (positionGiftCard > 1) ? (positionGiftCard - 1) * giftCardHeight : 0;
      this.moveScrollTopGiftCardList(scrollTop);
    },
    moveScrollTopGiftCardList(scrollTop) {
      this.timeout = setTimeout(() => {
        this.$refs.giftCardListContainer.$el.scrollTo({ top: scrollTop, behavior: 'instant' });
      }, 0);
    },
  },
};
</script>

<template>
  <div>
    <section
      ref="containerTitleCardList"
      class="giftcard-list__header"
      data-ui="gift-card-list-container"
    >
      <cdr-text data-ui="gift-card-select-design">
        Select card design*
      </cdr-text>
    </section>
    <cdr-grid
      v-if="giftCards.length > 0"
      ref="giftCardListContainer"
      :class="`giftcard-list__grid ${
        (!isMobile && isGiftCardSelect) ? 'form-view' : ''
      }`"
      :style="isGiftCardSelect && !isMobile ? {'height': giftCardListHeight} : {}"
      tag="section"
      gutter="small"
      role="radiogroup"
      data-ui="gift-card-list-images-container"
    >
      <button-img-component
        v-for="(giftCard, idx) in giftCards"
        :key="giftCard.mediaCode"
        :media-code="giftCard.mediaCode"
        :media-path="giftCard.mediaPath"
        :description="giftCard.description"
        :selected="giftCard.selected"
        :index="idx"
        @selectCard="selectGiftCard"
      />
    </cdr-grid>
    <cdr-button
      v-if="isMobile && totalItem > 12"
      class="giftcard-list__show-btn"
      modifier="link"
      data-ui="gift-card-btn-show-more"
      :full-width="true"
      @click="showLessMoreGiftCards"
    >
      {{ getTitleShowMore }}
    </cdr-button>
  </div>
</template>
